import React, { useState, useEffect, useCallback, useRef, createContext, useContext, type ReactNode } from 'react';

import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  DeleteOutlined,
  StopOutlined,
  DownloadOutlined,
  EyeOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Modal, List, Progress, Tag, Space, Button, Typography, message as antMessage, notification, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { translateErrorInterpolations } from '@utils/translateErrorInterpolations';

const { Text } = Typography;

type JobStatus = 'started' | 'processing' | 'completed' | 'partially_completed' | 'failed';

interface JobProgress {
  current: number;
  total: number;
}

interface BackgroundJobData {
  import_id?: string;
  import_name?: string;
  picking_list_id?: string;
  download_url?: string;
  // i18n key rendered by BackgroundJobPanel after a job finishes — workers set this
  // in terminal_data to surface a result line (e.g. "刪除 30 個 / 略過 12 個") in the
  // panel. Interpolations are taken from the rest of `data`.
  result_message_key?: string;
  [key: string]: unknown;
}

type AddJobOnComplete = (data?: BackgroundJobData) => void;
type AddJobOnError = (
  data?: BackgroundJobData,
  error?: string | number,
  interpolations?: Record<string, unknown>,
) => void;

interface AddJobOptions {
  nameParams?: Record<string, unknown> | null;
  onComplete?: AddJobOnComplete | null;
  onError?: AddJobOnError | null;
}

interface BackgroundJob {
  id: string;
  name: string;
  nameParams?: Record<string, unknown> | null;
  status: JobStatus;
  message?: string;
  progress?: JobProgress;
  createdAt: Date | string;
  updatedAt?: Date;
  completedAt?: Date | null;
  data?: BackgroundJobData;
  error?: string | number;
  interpolations?: Record<string, unknown>;
  onComplete?: AddJobOnComplete | null;
  onError?: AddJobOnError | null;
  [key: string]: unknown;
}

interface BackgroundJobNotificationPayload {
  status: JobStatus;
  message?: string;
  progress?: JobProgress;
  error?: string | number;
  interpolations?: Record<string, unknown>;
  data?: BackgroundJobData;
  completed_at?: string;
}

interface NotificationHookReturn {
  notificationData: Record<string, BackgroundJobNotificationPayload>;
  setModalVisible: (visible: boolean) => void;
  registerJob: (...args: unknown[]) => void;
}

// useBackgroundJobNotification.jsx 仍是 .jsx，allowJs 下其 return 被推為 Object；
// 這裡用寬鬆形式接受，內部 destructure 後當作 NotificationHookReturn 使用。
type NotificationHook = () => NotificationHookReturn | Record<string, unknown> | object;

// admin / operator 各自的 apiFetch 簽名略有差異（OperatorFetchOptions vs RequestInit），
// 這裡以最小可行介面對齊：能接受 (path, init) 並回傳 Response。
type ApiFetch = (path: string, init?: any) => Promise<Response>;

interface CableManager {
  isWebSocketAlive: () => boolean;
}

type AddJobFn = (
  jobId: string,
  jobName?: string,
  optionsOrOnComplete?: AddJobOnComplete | AddJobOptions | null,
) => void;

interface BackgroundJobContextValue {
  jobs: BackgroundJob[];
  addJob: AddJobFn;
  removeJob: (jobId: string) => void;
  clearCompletedJobs: () => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  jobCount: number;
  hasRunningJobs: boolean;
  hasFailedJobs: boolean;
  apiFetch: ApiFetch;
  syncPendingJobs: () => Promise<void>;
  notificationData: Record<string, BackgroundJobNotificationPayload>;
  registerJob: (...args: unknown[]) => void;
}

interface JobStatusConfigItem {
  color: string;
  icon: ReactNode;
  text: string;
}

// 🔥 建立 Context（不匯出，只在內部使用）
const BackgroundJobContext = createContext<BackgroundJobContextValue | null>(null);

/**
 * Common BackgroundJobManager - 背景任務管理器（通用版）
 *
 * 功能：
 * - 顯示所有正在執行和已完成的背景任務
 * - 實時更新任務進度（透過 Action Cable）
 * - 支援多個任務同時執行
 * - 提供任務狀態視覺化
 * - 支援 Admin 和 Operator 角色
 *
 * 使用方式：
 * ```tsx
 * // 在 Admin 中使用
 * const { addJob, jobCount } = useAdminBackgroundJob();
 *
 * // 在 Operator 中使用
 * const { addJob, jobCount } = useOperatorBackgroundJob();
 * ```
 */

// Job 狀態配置（使用函數以支援 i18n）
type TranslateFn = (key: string, defaultValue?: string | Record<string, unknown>, options?: Record<string, unknown>) => string;
const getJobStatusConfig = (t: TranslateFn): Record<JobStatus, JobStatusConfigItem> => ({
  started: {
    color: 'blue',
    icon: <SyncOutlined spin />,
    text: t('background_job.status.started', '已開始'),
  },
  processing: {
    color: 'processing',
    icon: <SyncOutlined spin />,
    text: t('background_job.status.processing', '處理中'),
  },
  completed: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    text: t('background_job.status.completed', '已完成'),
  },
  partially_completed: {
    color: 'warning',
    icon: <WarningOutlined />,
    text: t('background_job.status.partially_completed', '部分完成'),
  },
  failed: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    text: t('background_job.status.failed', '失敗'),
  },
});

// 從 localStorage 讀取任務
const loadJobsFromStorage = (storageKey: string): BackgroundJob[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const jobs = JSON.parse(stored) as BackgroundJob[];
      // 過濾掉超過 24 小時的任務
      const now = new Date().getTime();
      const filtered = jobs.filter((job) => {
        const createdAt = new Date(job.createdAt).getTime();
        return now - createdAt < 24 * 60 * 60 * 1000;
      });
      return filtered;
    }
  } catch (error) {
    console.error('[BackgroundJobManager] Failed to load jobs from storage:', error);
  }
  return [];
};

// 保存任務到 localStorage（儲存所有任務，排除 onComplete/onError callback）
const saveJobsToStorage = (storageKey: string, jobs: BackgroundJob[]): void => {
  try {
    const jobsToSave = jobs.map((j) => ({
      id: j.id,
      name: j.name,
      nameParams: j.nameParams, // 儲存 i18n 參數
      status: j.status,
      message: j.message,
      progress: j.progress,
      createdAt: j.createdAt,
      updatedAt: j.updatedAt,
      completedAt: j.completedAt,
      data: j.data,
      error: j.error,
      interpolations: j.interpolations,
      // onComplete / onError 不儲存（function 無法序列化）
    }));
    localStorage.setItem(storageKey, JSON.stringify(jobsToSave));
  } catch (error) {
    console.error('[BackgroundJobManager] Failed to save jobs to storage:', error);
  }
};

interface BackgroundJobProviderProps {
  children: ReactNode;
  useNotificationHook: NotificationHook;
  apiFetch: ApiFetch;
  storageKey: string;
  cableManager?: CableManager;
}

/**
 * BackgroundJobProvider - 背景任務 Context Provider
 */
export function BackgroundJobProvider({ children, useNotificationHook, apiFetch, storageKey, cableManager }: BackgroundJobProviderProps) {
  const { t } = useTranslation();
  // 初始化：從 localStorage 載入任務
  const [jobs, setJobs] = useState<BackgroundJob[]>(() => loadJobsFromStorage(storageKey));
  const [visible, setVisible] = useState(false);

  // 🔥 暫存 race-arrived notifications：notification 比 addJob 更早抵達時用來 buffer
  // Key = String(jobId)，Value = 完整 notification data（同 jobId 只保留最新，覆寫）
  // race condition 來源：Sidekiq worker 飛快完成 (<0.5s)，WebSocket completed
  // 通知抵達時 jobs state 還沒寫入該 jobId，被 silent ignore，導致 onComplete 永不觸發
  //
  // 🔥 同 jobId 多筆通知連續 race 時，buffer 只保留最新（後到覆寫前到）。
  // 這代表進度更新（progressing 50% → completed）的中間狀態會被丟棄，
  // addJob replay 時直接從 started 跳到 terminal status；
  // 這是預期行為，因為 onComplete callback 只關心終態。
  const pendingNotificationsRef = useRef<Record<string, BackgroundJobNotificationPayload>>({});

  // 🔥 processNotification 在下方才定義（依賴 showImportNotification），用 ref 讓
  // addJob 也能呼叫到，避免重排整個 component 順序
  const processNotificationRef = useRef<((jobId: string, data: BackgroundJobNotificationPayload, prevJobs: BackgroundJob[]) => BackgroundJob[]) | null>(null);

  // ✅ 一進入後台就訂閱 WebSocket，不傳入 visible
  const { notificationData, setModalVisible, registerJob } = useNotificationHook() as NotificationHookReturn;

  // ✅ 當 Modal visible 變化時，更新 notification hook 的狀態
  useEffect(() => {
    setModalVisible(visible);
  }, [visible, setModalVisible]);

  // 當 jobs 變化時，自動保存到 localStorage
  useEffect(() => {
    saveJobsToStorage(storageKey, jobs);
  }, [jobs, storageKey]);


  // 添加新任務（不自動打開 Modal）
  // @param jobId - 任務 ID
  // @param jobName - 任務名稱（可以是 i18n key 或純文字）
  // @param optionsOrOnComplete - 選項物件 { nameParams, onComplete, onError } 或 onComplete callback
  const addJob = useCallback<AddJobFn>((jobId, jobName, optionsOrOnComplete = null) => {
    // 檢查 ActionCable WebSocket 連線狀態
    if (cableManager) {
      const wsAlive = cableManager.isWebSocketAlive();
      if (!wsAlive) {
        console.error(
          '[BackgroundJobManager] ActionCable WebSocket is disconnected! ' +
          `Job "${jobId}" has been submitted, but real-time progress updates will NOT be received. ` +
          'The job will still execute on the server. Results can be retrieved when the connection is restored.'
        );
      }
    }
    // 支援舊的 API (jobId, jobName, onComplete) 和新的 API (jobId, jobName, { nameParams, onComplete, onError })
    let nameParams: Record<string, unknown> | null = null;
    let onComplete: AddJobOnComplete | null = null;
    let onError: AddJobOnError | null = null;
    if (typeof optionsOrOnComplete === 'function') {
      onComplete = optionsOrOnComplete;
    } else if (optionsOrOnComplete && typeof optionsOrOnComplete === 'object') {
      nameParams = optionsOrOnComplete.nameParams || null;
      onComplete = optionsOrOnComplete.onComplete || null;
      onError = optionsOrOnComplete.onError || null;
    }

    setJobs((prev) => {
      // 檢查是否已存在
      if (prev.find((j) => j.id === jobId)) {
        return prev;
      }

      const newJob: BackgroundJob = {
        id: jobId,
        name: jobName || 'background_job.title',
        nameParams, // 儲存 i18n 參數以便動態翻譯
        status: 'started',
        message: 'background_job.messages.start',
        progress: { current: 0, total: 100 },
        createdAt: new Date(),
        onComplete, // 儲存 callback
        onError, // 儲存 error callback
      };

      const withNewJob = [...prev, newJob];

      // 🔥 race condition fix：檢查 buffer 是否已有對應 notification
      // 若 worker 飛快完成、completed 通知早於 addJob 抵達，這裡 replay 一次
      // 確保 onComplete 被觸發、auto-download / 匯入通知等副作用照常執行
      const bufferedKey = String(newJob.id);
      const bufferedData = pendingNotificationsRef.current[bufferedKey];
      if (bufferedData && processNotificationRef.current) {
        console.log(`[BackgroundJobManager] Replayed buffered notification: ${newJob.id}`);
        const replayed = processNotificationRef.current(newJob.id, bufferedData, withNewJob);
        delete pendingNotificationsRef.current[bufferedKey];
        return replayed;
      }

      return withNewJob;
    });
  }, [cableManager]);

  // 移除任務
  const removeJob = useCallback((jobId: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  // 清除所有已完成或失敗的任務
  const clearCompletedJobs = useCallback(() => {
    setJobs((prev) =>
      prev.filter(
        (j) =>
          j.status !== 'completed' &&
          j.status !== 'partially_completed' &&
          j.status !== 'failed'
      )
    );
  }, []);

  // 🔥 匯入任務通知（成功/失敗共用）
  // @param type - 通知類型
  // @param jobId - Job ID
  // @param job - Job 物件（含 name, nameParams）
  // @param jobData - 任務回傳資料（含 import_id, import_name）
  // @param errorMsg - 錯誤訊息（僅 error 時使用）
  const showImportNotification = useCallback((
    type: 'success' | 'error' | 'partial',
    jobId: string,
    job: BackgroundJob | undefined,
    jobData: BackgroundJobData | undefined,
    errorMsg?: string | number,
  ) => {
    // 計算 basePath（admin 或 operator）
    const basePath = window.location.pathname.startsWith('/operator') ? '/operator' : '/admin';

    // 翻譯任務名稱（如果是 i18n key）
    const jobDisplayName = job?.name?.includes('.') && !job?.name?.includes(' ')
      ? t(job.name, job.nameParams || {})
      : (job?.name || t('background_job.title'));

    const importName = jobData?.import_name || `#${jobData?.import_id}`;
    const importLink = `${basePath}/imports/${jobData?.import_id}`;

    if (type === 'error') {
      // 翻譯錯誤訊息（如果是 i18n key）
      const errorMsgStr = typeof errorMsg === 'string' ? errorMsg : (errorMsg != null ? String(errorMsg) : undefined);
      const errorText = errorMsgStr?.includes('.') && !errorMsgStr?.includes(' ')
        ? t(errorMsgStr)
        : (errorMsgStr || t('background_job.messages.fail'));

      notification.error({
        message: `${jobDisplayName} ${t('background_job.status.failed')}`,
        description: (
          <div>
            <div>{errorText}</div>
            <div style={{ marginTop: 8 }}>
              {t('background_job.import_error_check_prefix')}{' '}
              <a href={importLink}>{importName}</a>{' '}
              {t('background_job.import_error_check_suffix')}
            </div>
          </div>
        ),
        duration: 0, // 不自動消失
      });
    } else if (type === 'partial') {
      notification.warning({
        message: `${jobDisplayName} ${t('background_job.status.partially_completed')}`,
        description: (
          <div>
            <div>{t('background_job.import_partial_description', '部分資料匯入失敗，請至匯入紀錄確認')}</div>
            <div style={{ marginTop: 8 }}>
              <a href={importLink}>{importName}</a>
            </div>
          </div>
        ),
        duration: 0, // 不自動消失
      });
    } else {
      notification.success({
        message: `${jobDisplayName} ${t('background_job.status.completed')}`,
        description: (
          <div>
            <a href={importLink}>{importName}</a>{' '}
            {t('background_job.import_success_check_suffix')}
          </div>
        ),
      });
    }
  }, [t]);

  // 🔥 處理單筆 notification 的核心邏輯（從 setJobs reducer 抽出）
  // - 若 job 不存在於 prevJobs，直接 return prevJobs（caller 自行決定是否 buffer）
  // - 副作用（auto-download、antMessage、showImportNotification、callback）保留在此
  //   原本就在 setJobs reducer 內執行，事件驅動，這裡逐字搬移行為
  const processNotification = useCallback((jobId: string, data: BackgroundJobNotificationPayload, prevJobs: BackgroundJob[]): BackgroundJob[] => {
    const index = prevJobs.findIndex((j) => j.id === jobId || j.id === String(jobId));

    // 如果任務不存在，回傳 prevJobs，由 caller 決定是否要 buffer
    if (index === -1) {
      return prevJobs;
    }

    const oldJob = prevJobs[index];

    // 跳過已處於終態且狀態未變的 job（避免重複處理累積的舊通知）
    const TERMINAL_STATUSES: JobStatus[] = ['completed', 'partially_completed', 'failed'];
    if (TERMINAL_STATUSES.includes(oldJob.status) && oldJob.status === data.status) {
      return prevJobs;
    }

    const newJobs = [...prevJobs];

    console.log(`[BackgroundJobManager] 🔄 Updating Job ${jobId} status: ${oldJob.status} -> ${data.status}`);

    newJobs[index] = {
      ...oldJob,
      status: data.status,
      message: data.message || oldJob.message,
      progress: data.progress || oldJob.progress,
      error: data.error,
      interpolations: data.interpolations || oldJob.interpolations,
      data: data.data,
      updatedAt: new Date(),
      completedAt: data.completed_at ? new Date(data.completed_at) : oldJob.completedAt,
      onComplete: oldJob.onComplete, // 保留 callback
      onError: oldJob.onError, // 保留 error callback
    };

    // 🔥 任務完成時的處理（completed 與 partially_completed 都算「剛結束」）
    const justFinished =
      (data.status === 'completed' || data.status === 'partially_completed') &&
      oldJob.status !== 'completed' &&
      oldJob.status !== 'partially_completed';

    if (justFinished) {
      console.log(`[BackgroundJobManager] 🎉 Job ${jobId} finished with status=${data.status}. Triggering logic...`);
      // 觸發 onComplete callback
      if (oldJob.onComplete && typeof oldJob.onComplete === 'function') {
        console.log(`[BackgroundJobManager] 📞 Executing onComplete callback for Job ${jobId}`);
        try {
          oldJob.onComplete(data.data);
        } catch (error) {
          console.error('[BackgroundJobManager] Error in onComplete callback:', error);
        }
      } else {
        console.log(`[BackgroundJobManager] ℹ️ No onComplete callback found for Job ${jobId}`);
      }

      // 匯入完成通知（有 import_id 代表是匯入任務）
      if (data.data?.import_id) {
        const notifType = data.status === 'partially_completed' ? 'partial' : 'success';
        showImportNotification(notifType, jobId, oldJob, data.data);
      }

      // 自動下載：當任務有 download_url 時
      if (data.data?.download_url) {
        // 🔥 只有在 active tab (focus 狀態) 時才自動下載
        const isActiveTab = !document.hidden && document.visibilityState === 'visible';
        const downloadUrl = data.data.download_url;
        const isPdf = downloadUrl.toLowerCase().includes('.pdf');

        if (isActiveTab) {
          // 使用 <a> 標籤觸發下載（window.open 會被 popup blocker 攔截，
          // 因為這是從 WebSocket callback 觸發，非使用者點擊事件）
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          if (!isPdf) {
            link.download = ''; // 非 PDF 觸發下載而非開啟
          }
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          antMessage.success(
            isPdf
              ? t('background_job.messages.pdf_opened', 'PDF 已在新分頁開啟')
              : t('background_job.messages.file_downloading', '檔案已開始下載')
          );
        } else {
          // 背景分頁不自動下載，只顯示通知
          antMessage.info(t('background_job.messages.export_complete_click_download', '匯出完成！請點擊背景任務面板中的下載按鈕取得檔案'), 5);
        }
      }
    }

    // 🔥 任務失敗時的處理：顯示持久通知（不自動消失）
    if (oldJob.status !== 'failed' && data.status === 'failed') {
      console.log(`[BackgroundJobManager] ❌ Job ${jobId} FAILED.`);
      // 觸發 onError callback
      if (oldJob.onError && typeof oldJob.onError === 'function') {
        console.log(`[BackgroundJobManager] 📞 Executing onError callback for Job ${jobId}`);
        try {
          oldJob.onError(data.data, data.error, data.interpolations);
        } catch (error) {
          console.error('[BackgroundJobManager] Error in onError callback:', error);
        }
      } else {
        console.log(`[BackgroundJobManager] ℹ️ No onError callback found for Job ${jobId}`);
      }

      if (data.data?.import_id) {
        showImportNotification('error', jobId, oldJob, data.data, data.error);
      }
    }

    return newJobs;
  }, [t, showImportNotification]);

  // 🔥 同步 ref，讓 addJob 也能呼叫到（避免重排 component 內函數順序）
  // 放進 useEffect：React 慣例 ref mutation 不放在 render phase，concurrent mode
  // 下 render 可能重跑，雖然這裡不會出錯但破壞慣例。
  useEffect(() => {
    processNotificationRef.current = processNotification;
  }, [processNotification]);

  // 監聽 notificationData 變化並更新 jobs
  useEffect(() => {
    Object.entries(notificationData).forEach(([jobId, data]) => {
      console.log(`[BackgroundJobManager] 📡 Processing notification for Job ${jobId}:`, data.status);

      // 🔥 race condition fix：先把 notification 存進 buffer 作為 fallback
      // 若此時 job 還沒被 addJob 寫入（worker 飛快完成），addJob 會在寫入後 replay
      pendingNotificationsRef.current[String(jobId)] = data;

      setJobs((prev) => {
        const newJobs = processNotification(jobId, data, prev);
        if (newJobs === prev) {
          // job 不存在或無需更新；buffer 已存，靜默
          return prev;
        }
        // 已成功處理就清掉 buffer，避免之後 addJob 重複 replay
        delete pendingNotificationsRef.current[String(jobId)];
        return newJobs;
      });
    });
  }, [notificationData, processNotification]);

  // 🔥 unmount 時清空 buffer，避免跨 Provider 生命週期殘留
  useEffect(() => {
    return () => {
      pendingNotificationsRef.current = {};
    };
  }, []);

  // 需要關注的任務數（進行中 + 失敗），成功（含部分完成）已透過 toast 通知不計入
  const jobCount = jobs.filter(
    (j) => j.status !== 'completed' && j.status !== 'partially_completed'
  ).length;

  // 是否有正在執行的任務（控制 spin 動畫）
  const hasRunningJobs = jobs.some(
    (j) => j.status === 'started' || j.status === 'processing'
  );

  // 是否有已失敗但尚未清除的任務（控制 header icon 切換為警告樣式）
  const hasFailedJobs = jobs.some((j) => j.status === 'failed');


  // 🔥 同步未完成任務的狀態（供 Provider 呼叫）
  // 同時處理 sync 回補後發現已完成的任務（onComplete callback + auto-download）
  const syncPendingJobs = useCallback(async (): Promise<void> => {
    // 找出所有未完成的任務
    const pendingJobs = jobs.filter((job) =>
      job.status === 'started' || job.status === 'processing'
    );

    if (pendingJobs.length === 0) {
      return;
    }

    const jobIds = pendingJobs.map((job) => job.id);

    try {
      const response = await apiFetch('background_jobs/batch_status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ job_ids: jobIds }),
      });

      const result = await response.json() as { success: boolean; data: Record<string, BackgroundJobNotificationPayload> };

      if (result.success) {
        const statuses = result.data;

        setJobs((prevJobs) => {
          const newJobs = prevJobs
            .map((job): BackgroundJob => {
              // 只處理未完成的任務
              if (job.status !== 'started' && job.status !== 'processing') {
                return job;
              }

              const backendStatus = statuses[job.id];

              // 任務在後端不存在（已過期或被刪除）
              if (!backendStatus) {
                return {
                  ...job,
                  status: 'failed',
                  message: t('background_job.messages.job_expired', '任務已過期或不存在'),
                  error: t('background_job.messages.job_not_found_error', '無法從後端找到此任務') as string,
                  updatedAt: new Date(),
                };
              }

              const updatedJob: BackgroundJob = {
                ...job,
                status: backendStatus.status,
                message: backendStatus.message || job.message,
                progress: backendStatus.progress || job.progress,
                data: backendStatus.data ? {
                  ...job.data,
                  ...backendStatus.data
                } : job.data,
                error: backendStatus.error,
                interpolations: backendStatus.interpolations || job.interpolations,
                updatedAt: new Date(),
              };

              // 透過 sync 發現任務已完成：觸發 onComplete callback + auto-download
              const prevStatus: string = job.status;
              const syncJustFinished =
                (backendStatus.status === 'completed' || backendStatus.status === 'partially_completed') &&
                prevStatus !== 'completed' &&
                prevStatus !== 'partially_completed';

              if (syncJustFinished) {
                console.log(`[BackgroundJobManager] Sync found finished job: ${job.id} (status=${backendStatus.status})`);

                if (job.onComplete && typeof job.onComplete === 'function') {
                  try {
                    job.onComplete(backendStatus.data);
                  } catch (error) {
                    console.error('[BackgroundJobManager] Error in onComplete callback (sync):', error);
                  }
                }

                // 匯入完成通知
                if (backendStatus.data?.import_id) {
                  const notifType = backendStatus.status === 'partially_completed' ? 'partial' : 'success';
                  showImportNotification(notifType, job.id, job, backendStatus.data);
                }

                // Auto-download（tab 回到前景時觸發 sync，此時一定是 visible）
                const downloadUrl = backendStatus.data?.download_url;
                if (downloadUrl) {
                  const isPdf = downloadUrl.toLowerCase().includes('.pdf');
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  if (!isPdf) {
                    link.download = '';
                  }
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  antMessage.success(
                    isPdf
                      ? t('background_job.messages.pdf_opened', 'PDF 已在新分頁開啟')
                      : t('background_job.messages.file_downloading', '檔案已開始下載')
                  );
                } else if (!backendStatus.data?.import_id) {
                  antMessage.success(t('background_job.messages.complete', '任務已完成'));
                }
              }

              // 透過 sync 發現任務失敗：顯示持久通知
              if (backendStatus.status === 'failed' && prevStatus !== 'failed') {
                console.log(`[BackgroundJobManager] Sync found failed job: ${job.id}`);
                // 觸發 onError callback
                if (job.onError && typeof job.onError === 'function') {
                  try {
                    job.onError(backendStatus.data, backendStatus.error, backendStatus.interpolations);
                  } catch (error) {
                    console.error('[BackgroundJobManager] Error in onError callback (sync):', error);
                  }
                }

                if (backendStatus.data?.import_id) {
                  showImportNotification('error', job.id, job, backendStatus.data, backendStatus.error);
                }
              }

              return updatedJob;
            });

          return newJobs;
        });
      }
    } catch (error) {
      console.error('[BackgroundJobManager] Failed to sync jobs:', error);
    }
  }, [jobs, apiFetch, t, showImportNotification]);

  // 🔥 Tab 切回前景時，自動同步未完成任務的狀態
  // 解決：背景任務在 tab unfocus 期間完成，WebSocket 訊息遺失的問題
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) return;

      const hasPendingJobs = jobs.some(
        (j) => j.status === 'started' || j.status === 'processing'
      );

      if (hasPendingJobs) {
        console.log('[BackgroundJobManager] Tab visible, syncing pending jobs...');
        syncPendingJobs();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [jobs, syncPendingJobs]);

  // 🔥 Context value - 每次 jobs, jobCount, visible, notificationData 變化時都會重新建立
  const contextValue: BackgroundJobContextValue = {
    jobs,
    addJob,
    removeJob,
    clearCompletedJobs,
    visible,
    setVisible,
    jobCount,
    hasRunningJobs,
    hasFailedJobs,
    apiFetch,
    syncPendingJobs,
    notificationData,  // ✅ 包含所有通知類型（job_status + new_notification）
    registerJob,       // ✅ 匯出註冊函數
  };

  return (
    <BackgroundJobContext.Provider value={contextValue}>
      {children}
    </BackgroundJobContext.Provider>
  );
}

/**
 * useBackgroundJob - 使用背景任務的 Hook
 */
export function useBackgroundJob(): BackgroundJobContextValue {
  const context = useContext(BackgroundJobContext);

  if (!context) {
    throw new Error('useBackgroundJob must be used within BackgroundJobProvider');
  }

  return context;
}

interface BackgroundJobPanelProps {
  jobs: BackgroundJob[];
  onRemoveJob: (jobId: string) => void;
  onClearCompleted: () => void;
  onClose: () => void;
  apiFetch: ApiFetch;
}

/**
 * BackgroundJobPanel - 背景任務面板組件
 */
export function BackgroundJobPanel({ jobs, onRemoveJob, onClearCompleted, onClose, apiFetch }: BackgroundJobPanelProps) {
  const { t } = useTranslation();
  const [cancellingJobs, setCancellingJobs] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // 翻譯訊息（如果是 i18n key 則翻譯，否則直接顯示）
  const translateMessage = (message: string | undefined, progress?: Record<string, unknown>): string => {
    if (!message) return '';
    // 檢查是否為 i18n key 格式（包含點號且不含空格）
    if (message.includes('.') && !message.includes(' ')) {
      return t(message, progress || {}) as string;
    }
    return message;
  };

  const getProgress = (job: BackgroundJob): number => {
    if (!job.progress) return 0;
    const { current, total } = job.progress;
    if (!total || total === 0) return 0;
    return Math.round((current / total) * 100);
  };

  const getStatusConfig = (status: JobStatus): JobStatusConfigItem => {
    const statusConfig = getJobStatusConfig(t as unknown as TranslateFn);
    return statusConfig[status] || statusConfig.processing;
  };

  const hasCompletedJobs = jobs.some(
    (j) => j.status === 'completed' || j.status === 'partially_completed' || j.status === 'failed'
  );

  // 取消任務
  const handleCancelJob = async (jobId: string) => {
    setCancellingJobs((prev) => new Set(prev).add(jobId));

    try {
      const response = await apiFetch(`background_jobs/${jobId}/cancel`, {
        method: 'DELETE',
      });
      const data = await response.json() as { success: boolean; message?: string };

      if (data.success) {
        antMessage.success(t('background_job.messages.job_canceled', '任務已取消'));
      } else {
        antMessage.error(data.message || t('background_job.messages.cancel_job_failed', '取消任務失敗'));
      }
    } catch (error) {
      console.error('取消任務錯誤:', error);
      antMessage.error(t('background_job.messages.cancel_job_failed', '取消任務失敗'));
    } finally {
      setCancellingJobs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  return (
    <Modal
      title={
        <Space>
          <span>{t('background_job.title', '背景任務')}</span>
          <Tag color="blue">{t('background_job.task_count', '{{count}} 個任務', { count: jobs.length }) as string}</Tag>
        </Space>
      }
      open={true}
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClearCompleted} disabled={!hasCompletedJobs}>
            {t('background_job.clear_completed', '清除已完成')}
          </Button>
          <Button type="primary" onClick={onClose}>
            {t('background_job.close', '關閉')}
          </Button>
        </Space>
      }
      width={600}
    >
      {jobs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          {t('background_job.no_tasks', '目前沒有任務')}
        </div>
      ) : (
        <List
          dataSource={[...jobs].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())}
          renderItem={(job: BackgroundJob) => {
            const statusConfig = getStatusConfig(job.status);
            const progress = getProgress(job);
            const errorStr = typeof job.error === 'string' ? job.error : (job.error != null ? String(job.error) : '');

            return (
              <List.Item
                key={job.id}
                actions={[
                  // 進行中的任務顯示停止按鈕
                  (job.status === 'started' || job.status === 'processing') && (
                    <Popconfirm
                      title={t('background_job.cancel_confirm_title', '確定要取消此任務嗎？')}
                      description={t('background_job.cancel_confirm_description', '任務將會被停止，此操作無法復原')}
                      onConfirm={() => handleCancelJob(job.id)}
                      okText={t('background_job.confirm', '確定')}
                      cancelText={t('background_job.cancel', '取消')}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<StopOutlined />}
                        loading={cancellingJobs.has(job.id)}
                        danger
                      />
                    </Popconfirm>
                  ),
                  // 如果有 import_id，顯示查看匯入按鈕
                  (job.data?.import_id) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        navigate(`/imports/${job.data?.import_id}`);
                        onClose();
                      }}
                    >
                      {t('background_job.view_import', '查看匯入')} {job.data?.import_name && `(${job.data.import_name})`}
                    </Button>
                  ),
                  // 如果有 picking_list_id，顯示查看撿貨單按鈕
                  (job.data?.picking_list_id) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        navigate(`/picking_lists/${job.data?.picking_list_id}`);
                        onClose();
                      }}
                    >
                      {t('background_job.view_picking_list', '查看撿貨單')}
                    </Button>
                  ),
                  // 錄影 session 強制合併成功後（fulfillment_video_id 是該 worker
                  // 才會有的成功標記）顯示「前往訂單」按鈕。Router 已在
                  // admin/operator SPA 各自設好 basename，相對路徑會自動帶上前綴。
                  // 合併過的 fulfillment 可能對到多個 order，只有正好 1 個時才顯示；
                  // 多 order 情境（合單）不提供 nav，操作員自行從清單找。
                  // 不導去 /fulfillments/:id：admin SPA 並無該路由（FulfillmentShow 只存在於
                  // operator SPA），按下會 404；走 /orders/:id 兩邊都通。
                  (job.data?.fulfillment_video_id && Array.isArray(job.data?.order_ids) && job.data.order_ids.length === 1) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        navigate(`/orders/${(job.data?.order_ids as string[])[0]}`);
                        onClose();
                      }}
                    >
                      {t('background_job.view_order', '前往訂單')}
                    </Button>
                  ),
                  // 已完成的任務如果有下載連結，顯示下載按鈕
                  ((job.status === 'completed' || job.status === 'partially_completed') && job.data?.download_url) && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      href={job.data.download_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('background_job.download', '下載')}
                    </Button>
                  ),
                  // 已完成或失敗的任務顯示刪除按鈕
                  (job.status === 'completed' || job.status === 'partially_completed' || job.status === 'failed') && (
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => onRemoveJob(job.id)}
                    />
                  ),
                ].filter(Boolean) as ReactNode[]}
              >
                <List.Item.Meta
                  avatar={statusConfig.icon}
                  title={
                    <Space>
                      <span>{translateMessage(job.name, job.nameParams || undefined)}</span>
                      <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {translateMessage(job.message, job.progress as unknown as Record<string, unknown>)}
                      </Text>
                      {job.status === 'processing' && job.progress && (
                        <Progress
                          percent={progress}
                          size="small"
                          status="active"
                          style={{ marginTop: 8 }}
                          format={(percent) =>
                            `${job.progress?.current}/${job.progress?.total} (${percent}%)`
                          }
                        />
                      )}
                      {(job.status === 'completed' || job.status === 'partially_completed') && job.data?.result_message_key && (
                        <Text style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {translateMessage(job.data.result_message_key, job.data as Record<string, unknown>)}
                        </Text>
                      )}
                      {(job.status === 'completed' || job.status === 'partially_completed') && job.completedAt && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {t('background_job.completed_at', '完成時間')}: {new Date(job.completedAt).toLocaleString()}
                        </Text>
                      )}
                      {job.status === 'failed' && errorStr && (
                        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {t('common.label.error', '錯誤')}: {
                            errorStr.includes('.')
                              ? translateMessage(errorStr, translateErrorInterpolations(t, job.interpolations))
                              : t(`common.error.${errorStr}`, { ...translateErrorInterpolations(t, job.interpolations), defaultValue: errorStr }) as string
                          }
                        </Text>
                      )}
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Modal>
  );
}

export default BackgroundJobPanel;
